/* CSS */
import "@js/parts/app-css";

// Imports
import Alpine from 'alpinejs';
import collapse from '@alpinejs/collapse';
import focus from '@alpinejs/focus';
import { DotLottie } from '@lottiefiles/dotlottie-web';
import Headroom from 'headroom.js';

// Project utils
import utils from '@js/utils/utils';

// Project helpers
import AnnouncementHeightCalc from '@js/helpers/AnnouncementHeightCalc';
import BodyScrollLock from '@js/helpers/BodyScrollLock';
import HeaderHeightCalc from '@js/helpers/HeaderHeightCalc';

// Project imports
import Carousels from '@js/modules/Carousels';
import HoverLink from '@js/modules/HoverLink';
import LocationMap from '@js/modules/LocationMap';
import { Modal } from '@js/modules/Modal';
import PageScrolling from '@js/modules/PageScrolling';
import PageScrollingManager from '@js/modules/PageScrollingManager';
import ScrollTo from '@js/modules/ScrollTo';
import SplitTextAnimator from '@js/modules/SplitTextAnimator';
import StickyPanels from '@js/modules/StickyPanels';

// Project Alpine JS plugins
import interactiveSitePlan from '@js/modules/InteractiveSitePlan';
import poiMap from '@js/modules/PoiMap';

/**
 * Alpine JS
 * Best to put it last so that all other potential JS is available
 * when components start getting initialized.
 */
Alpine.plugin(collapse);
Alpine.plugin(focus);
Alpine.data('interactiveSitePlan', interactiveSitePlan);
Alpine.data('poiMap', poiMap);
Alpine.magic('bodyScrollLock', () => BodyScrollLock);
window.Alpine = Alpine;
Alpine.start();

// Cross-document transitions can be skipped by the browser (for example when
// the navigation opt-in changes). Consume their promise rejections so a normal
// fallback navigation does not surface as an uncaught console error.
const consumeViewTransition = transition => {
    if (!transition) return;

    transition.ready.catch(() => {});
    transition.finished.catch(() => {});
};

const handleViewTransition = event => consumeViewTransition(event.viewTransition);

// `pagereveal` may fire before this module runs, but the transition remains
// available on the document until it finishes.
consumeViewTransition(document.activeViewTransition);
window.addEventListener('pagereveal', handleViewTransition);
window.addEventListener('pageswap', handleViewTransition);

window.APP =
    window.APP ||
    new (function APP() {
        // Utilities
        this.utils = utils;

        this.modules = {
            PageScrolling: PageScrolling,
            PageScrollingManager: PageScrollingManager,
        };

        // Helpers
        this.BodyScrollLock = BodyScrollLock;

        // Never leave a lock behind when navigating away
        window.addEventListener('pagehide', e => {
            BodyScrollLock.clear();
        });

        // Window Load
        window.addEventListener('load', e => {
            PageScrollingManager.update(true);
        });

        // DOM load
        document.addEventListener('DOMContentLoaded', e => {
            // Element height calculations
            AnnouncementHeightCalc.init();
            HeaderHeightCalc.init();


            window.headroomInstance;
            const header = document.querySelector('.mn-hdr');

            function initHeadroom() {
                if (window.headroomInstance) window.headroomInstance.destroy();

                window.headroomInstance = new Headroom(header, {
                    offset: window.innerHeight,
                    tolerance: {
                        up: 8,
                        down: 5
                    }
                });
                window.headroomInstance.init();
            }

            initHeadroom();

            // Debounce so you're not destroying/reiniting on every pixel of a drag-resize
            let resizeTimer;
            window.addEventListener('resize', () => {
                clearTimeout(resizeTimer);
                resizeTimer = setTimeout(initHeadroom, 150);
            });

            // Fade background videos in once their first frame is available.
            const backgroundVideos = document.querySelectorAll('.asset-bg-video');
            backgroundVideos.forEach(video => {
                const media = video.closest('.asset-bg-media');
                if (!media) return;

                const revealVideo = () => {
                    if (media.classList.contains('is-ready')) return;

                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => media.classList.add('is-ready'));
                    });
                };

                if (video.readyState >= 2) {
                    revealVideo();
                } else {
                    video.addEventListener('loadeddata', revealVideo, { once: true });
                    video.addEventListener('playing', revealVideo, { once: true });
                }
            });

            // Let fixed media reveals scroll away instead of being covered by the footer.
            const siteFooter = document.querySelector('.site-footer');
            const footerAwarePinnedBlocks = document.querySelectorAll('.block-assetBlock.is-fixed-asset');

            if (siteFooter && footerAwarePinnedBlocks.length) {
                const setFooterVisibility = isVisible => {
                    footerAwarePinnedBlocks.forEach(block => {
                        block.classList.toggle('is-footer-visible', isVisible);
                    });
                };
                const footerRect = siteFooter.getBoundingClientRect();

                setFooterVisibility(
                    footerRect.top < window.innerHeight && footerRect.bottom > 0
                );

                const footerObserver = new IntersectionObserver(([entry]) => {
                    setFooterVisibility(entry.isIntersecting);
                });

                footerObserver.observe(siteFooter);
            }


            // Home hero animation
            const homeHeroAnimation = document.getElementById('home-hero-animation');
            if (homeHeroAnimation) {
                // Run after 1 second
                setTimeout(() => {
                    const dotLottie = new DotLottie({
                        canvas: homeHeroAnimation,
                        src: '/assets/lottie/home/logo.json',
                        autoplay: true,
                        loop: false,
                    });
                }, 500);
            }

            // Split text animations
            const heroHeading = document.querySelector('.hero-heading-split-text');
            if (heroHeading) {
                const mobileHeadingTemplate = document.querySelector(
                    '[data-hero-heading-mobile]'
                );
                const desktopMarkup = heroHeading.innerHTML;
                const mobileMarkup = mobileHeadingTemplate?.innerHTML || desktopMarkup;
                const mobileMedia = window.matchMedia('(max-width: 47.999rem)');
                let heroHeadingSplitText = null;
                let renderedForMobile = null;

                const renderHeroHeading = (animateHeading = false) => {
                    const renderMobile = mobileMedia.matches && Boolean(mobileHeadingTemplate);
                    if (renderMobile === renderedForMobile) return;

                    heroHeadingSplitText?.destroy();
                    heroHeading.innerHTML = renderMobile ? mobileMarkup : desktopMarkup;
                    heroHeading.style.removeProperty('opacity');

                    if (animateHeading) {
                        heroHeadingSplitText = new SplitTextAnimator(heroHeading);
                    } else {
                        heroHeadingSplitText = null;
                        heroHeading.style.opacity = '1';
                        heroHeading.querySelectorAll('u').forEach(underline => {
                            underline.style.backgroundSize = '100% 2px';
                        });
                    }

                    renderedForMobile = renderMobile;
                };

                setTimeout(() => renderHeroHeading(true), 300);
                mobileMedia.addEventListener('change', () => renderHeroHeading());
            }

            // Carousels
            new Carousels();

            // To top button
            const toTopBtns = document.querySelectorAll('.to-top');
            toTopBtns.forEach(btn => {
                btn.addEventListener('click', (e) => {
                    window.scrollTo({
                        top: 0,
                        left: 0,
                        behavior: 'auto',
                    });
                });
            });

            /* Dialogss */
            const mailingListModal = new Modal('#mailing-list-modal');

            // Open mailing list modal
            const mailingListModalOpenEl = document.querySelector('.mailing-list-modal-open');
            if (mailingListModalOpenEl) {
                mailingListModalOpenEl.addEventListener('click', (e) => {
                    mailingListModal.open();
                });
            }

        });
    })();
